import React, { useEffect, useState, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../elements/Layout';
import { FaExclamationCircle, FaPlus, FaChevronLeft, FaChevronRight, FaEllipsisH } from 'react-icons/fa';
import logo from '../../assets/miles_logo.svg';
import ReactToPrint from 'react-to-print';
import Modal from '../../elements/Modal';
import Loading from '../../elements/Loading';
import AddNewPayment from './AddNewPayment';
import moment from 'moment';
import { deletePayment, editPayment, fetchCustomers, getMpesaPayments } from '../../utils/api/api';
import toast from 'react-hot-toast';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';
import Select from 'react-select';
import api from '../../utils/api/axiosInstance';
import { IoReceiptOutline, IoAdd, IoSearch, IoFilterOutline, IoCalendarOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../utils/context/AuthContext';
import { MdDeleteForever, MdFilterList, MdAttachMoney, MdPayments } from 'react-icons/md';
import { CiEdit } from 'react-icons/ci';
import { BsThreeDotsVertical, BsCreditCard2Front, BsCash, BsBank, BsCheckCircle, BsXCircle, BsClockHistory } from 'react-icons/bs';

const Payments = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [add, setAdd] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [viewReceipt, setViewReceipt] = useState(false);
    const [methodFilter, setMethodFilter] = useState('');
    const [customerIdFilter, setCustomerIdFilter] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const { user } = useContext(AuthContext);
    const printRef = useRef();
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [updatePayment, setUpdatePayment] = useState({
        id: null,
        amount: 0,
        method: '',
        transaction_id: '',
        transaction_number: '',
        createdAt: '',
    });
    const [updatingPayment, setUpdatingPayment] = useState(false);
    const [openDropdown, setOpenDropdown] = useState({});

    const handleToggle = (id) => {
        setOpenDropdown((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (Object.keys(openDropdown).some(id => openDropdown[id])) {
                setOpenDropdown({});
            }
        };
        document.addEventListener('click', handleClickOutside, { capture: true });
        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [openDropdown]);

    useEffect(() => {
        const loadCustomers = async () => {
            try {
                const response = await fetchCustomers({
                    page: 0,
                    pageSize: 0,
                });
                const formattedCustomers = response.customers.map(customer => ({
                    value: customer.id,
                    label: `${customer.first_name} ${customer.last_name}`,
                }));
                setCustomers(formattedCustomers);
            } catch (error) {
                toast.error('Failed to fetch customers.');
            }
        };

        loadCustomers();
    }, []);

    const printPDF = async () => {
        setLoading(true);
        try {
            const allData = await fetchPaymentsReports({ page: 1, pageSize: 1000 });
            const reportsData = allData.paymentReports;

            if (!reportsData || reportsData.length === 0) {
                toast.error("No payment reports available.");
                setLoading(false);
                return;
            }

            const doc = new jsPDF();
            doc.text("Payment Reports", 14, 10);

            const tableColumn = ["#", "Customer", "Method", "Transaction ID", "Amount", "Date", "Created By", "Is Manual"];

            const tableRows = reportsData.map((report, index) => [
                index + 1,
                `${report?.customer?.first_name || "N/A"} ${report?.customer?.last_name || ""}`.trim(),
                report.method,
                report.transaction_id,
                Number(report.amount).toLocaleString("en-US", { style: "currency", currency: "KES" }),
                moment(report.createdAt).format("MMM DD, YYYY, hh:mm A"),
                report.createdBy ? `${report.createdBy.first_name} ${report.createdBy.last_name}` : "Automated",
                report.is_manual ? "Yes" : "No",
            ]);

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 20,
            });

            doc.save("PaymentReports.pdf");

        } catch (error) {
            toast.error("Error generating PDF.");
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSelect = (selectedOption) => {
        setSelectedCustomer(selectedOption);
        setCustomerIdFilter(selectedOption?.value || null);
        setCurrentPage(1);
    };

    const handleMethodSelect = (e) => {
        setMethodFilter(e.target.value);
        setCurrentPage(1);
    };

    const getPayments = async (page, size) => {
        setLoading(true);
        const payload = {
            page,
            pageSize: size,
        };

        if (methodFilter) payload.method = methodFilter;
        if (customerIdFilter) payload.customer_id = customerIdFilter;
        try {
            const response = await getMpesaPayments(payload);

            setPayments(response.payments);
            setFilteredPayments(response.payments);
            setTotalPages(response.meta.totalPages);

        } catch (error) {
            toast.error(error.message || 'Request failed');
        } finally {
            setLoading(false);
        }
    };

    const [open, setOpen] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);

    const openDeleteModal = (paymentId, e) => {
        if (e) e.stopPropagation();
        setSelectedPaymentId(paymentId);
        setOpen(true);
    };

    const closeDeleteModal = () => {
        setSelectedPaymentId(null);
        setOpen(false);
    };

    const confirmDelete = async () => {
        try {
            await deletePayment(selectedPaymentId);
            toast.success('Payment deleted successfully');
            getPayments(currentPage, itemsPerPage);
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete payment');
        } finally {
            closeDeleteModal();
        }
    };

    const handleUpdatePayment = async () => {
        setLoading(true);
        try {
            await editPayment(updatePayment);
            toast.success('Payment updated successfully');
            setUpdatingPayment(false);
            getPayments(currentPage, itemsPerPage, customerIdFilter);
        } catch (error) {
            toast.error('Failed to update payment');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getPayments(currentPage, itemsPerPage);
    }, [currentPage, methodFilter, customerIdFilter, itemsPerPage]);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleJuniorAnalystApprove = async (paymentId) => {
        setLoading(true);
        try {
            const data = {
                id: paymentId.toString(),
                is_junior_analyst: true,
                is_finance: false,
                is_ceo: false,
                approve: 'accepted'
            };
            await api.post('/payments/approve', data);
            setPayments((prevPayments) =>
                prevPayments.map(payment =>
                    payment.id === paymentId ? { ...payment, junior_analyst_approved: 'accepted' } : payment
                )
            );
            toast.success('Payment approved by Junior Analyst');
            getPayments(currentPage, itemsPerPage, customerIdFilter);
        } catch (error) {
            toast.error('Failed to approve payment');
        } finally {
            setLoading(false);
        }
    };

    const handleFinanceApprove = async (paymentId) => {
        setLoading(true);
        try {
            const data = {
                id: paymentId.toString(),
                is_ceo: false,
                is_finance: true,
                is_junior_analyst: false,
                approve: 'accepted'
            };
            await api.post('/payments/approve', data);
            setLoading(false);
            toast.success('Payment approved by Finance');
            getPayments(currentPage, itemsPerPage, customerIdFilter);
        } catch (error) {
            toast.error('Failed to approve payment');
        } finally {
            setLoading(false);
        }
    };

    const handleCEOApprove = async (paymentId) => {
        setLoading(true);
        try {
            const data = {
                id: paymentId.toString(),
                is_ceo: true,
                is_finance: false,
                is_junior_analyst: false,
                approve: 'accepted'
            };
            await api.post('/payments/approve', data);
            setPayments((prevPayments) =>
                prevPayments.map(payment =>
                    payment.id === paymentId ? { ...payment, ceo_approved: 'accepted' } : payment
                )
            );
            toast.success('Payment approved by CEO');
            getPayments(currentPage, itemsPerPage, customerIdFilter);
        } catch (error) {
            toast.error('Failed to approve payment');
        } finally {
            setLoading(false);
        }
    };

    // Payment method icons
    const getMethodIcon = (method) => {
        switch (method) {
            case 'MPESA':
                return <BsCash className="text-green-600" />;
            case 'VISA':
                return <BsCreditCard2Front className="text-blue-600" />;
            case 'CHEQUE':
                return <BsBank className="text-purple-600" />;
            case 'BANK_TRANSFER':
                return <BsBank className="text-blue-600" />;
            case 'CASH':
                return <BsCash className="text-green-600" />;
            case 'WORLD_REMIT':
                return <MdAttachMoney className="text-amber-600" />;
            case 'RTGS':
                return <BsBank className="text-indigo-600" />;
            default:
                return <MdPayments className="text-gray-600" />;
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 300, damping: 24 }
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return Number(amount).toLocaleString("en-KE", {
            style: "currency",
            currency: "KES",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).replace("KES", "Kes");
    };

    return (
        <Layout text="Payments" backgroundColor='bg-white'>
            {loading && <Loading />}

            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="space-y-6"
            >
                {/* Header with filters and actions */}
                <motion.div
                    variants={itemVariants}
                    className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white/80 backdrop-blur-sm p-5 rounded-2xl shadow-sm"
                >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative"
                        >
                            <select
                                value={methodFilter}
                                onChange={handleMethodSelect}
                                className="pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200 w-full sm:w-auto min-w-[180px]"
                            >
                                <option value="">All Payment Methods</option>
                                <option value="MPESA">MPESA</option>
                                <option value="VISA">VISA</option>
                                <option value="CHEQUE">CHEQUE</option>
                                <option value="BANK_TRANSFER">BANK TRANSFER</option>
                                <option value="CASH">CASH</option>
                                <option value="WORLD_REMIT">WORLD REMIT</option>
                                <option value="RTGS">RTGS</option>
                            </select>
                            <MdFilterList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
                        </motion.div>

                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full sm:w-auto min-w-[220px]"
                        >
                            <Select
                                options={customers}
                                value={selectedCustomer}
                                onChange={handleCustomerSelect}
                                placeholder="Select a customer"
                                className="text-gray-700 rounded-xl focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        backgroundColor: 'rgb(249 250 251)',
                                        borderColor: 'rgb(229 231 235)',
                                        borderRadius: '0.75rem',
                                        padding: '0.375rem',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            borderColor: 'rgb(209 213 219)'
                                        }
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected
                                            ? 'var(--color-secondary)'
                                            : state.isFocused
                                                ? 'rgba(var(--color-secondary-rgb), 0.1)'
                                                : null,
                                        color: state.isSelected ? 'white' : 'inherit'
                                    })
                                }}
                            />
                        </motion.div>
                    </div>

                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setAdd(true)}
                        className="flex items-center gap-2 px-5 py-3 bg-secondary text-white rounded-xl shadow-sm hover:bg-secondary/90 transition-colors w-full sm:w-auto justify-center sm:justify-start"
                    >
                        <IoAdd size={18} />
                        <span>Add Payment</span>
                    </motion.button>
                </motion.div>

                {/* Payments Table */}
                <motion.div
                    variants={itemVariants}
                    className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
                >
                    {loading ? (
                        <div className="p-8 flex justify-center items-center">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full"
                            />
                        </div>
                    ) : filteredPayments && filteredPayments.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid By</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Junior Analyst</th>
                                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Finance</th>
                                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">CEO</th>
                                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredPayments.map((payment, index) => (
                                        <motion.tr
                                            key={payment.id}
                                            // initial={{ opacity: 0, y: 10 }}
                                            // animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.03 }}
                                            whileHover={{ backgroundColor: "#f9fafb" }}
                                            className="group bg-white cursor-pointer"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {index + 1 + (currentPage - 1) * itemsPerPage}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 flex-shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center uppercase">
                                                        {payment?.customer?.first_name?.[0] || '?'}
                                                    </div>
                                                    <div className="ml-3">
                                                        <div className="text-sm font-medium text-gray-900 uppercase">
                                                            {`${payment?.customer?.first_name || ''} ${payment?.customer?.last_name || ''}`
                                                                .split(" ")
                                                                .slice(0, 2)
                                                                .join(" ")}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="mr-2">
                                                        {getMethodIcon(payment.method)}
                                                    </div>
                                                    <span className="text-sm font-bold text-gray-900">
                                                        {formatCurrency(payment?.amount)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <IoCalendarOutline className="mr-2 text-gray-500" />
                                                    <span className="text-sm text-gray-700">
                                                        {moment(payment.createdAt).format("MMM D, YYYY")}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {moment(payment.createdAt).format("h:mm A")}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                {user.role.is_junior_analyst && payment.junior_analyst_approved !== "accepted" ? (
                                                    <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        onClick={() => handleJuniorAnalystApprove(payment.id)}
                                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary"
                                                    >
                                                        <BsCheckCircle className="mr-1" />
                                                        Approve
                                                    </motion.button>
                                                ) : payment.junior_analyst_approved === "accepted" ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <BsCheckCircle className="mr-1" />
                                                        Approved
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                        <BsClockHistory className="mr-1" />
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center z-0">
                                                {user.role.is_finance && payment.junior_analyst_approved === "accepted" && payment.finance_approved !== "accepted" ? (
                                                    <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        onClick={() => handleFinanceApprove(payment.id)}
                                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary"
                                                    >
                                                        <BsCheckCircle className="mr-1" />
                                                        Approve
                                                    </motion.button>
                                                ) : payment.finance_approved === "accepted" ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <BsCheckCircle className="mr-1" />
                                                        Approved
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                        <BsClockHistory className="mr-1" />
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                {user.role.is_ceo && payment.finance_approved === "accepted" && payment.ceo_approved !== "accepted" ? (
                                                    <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        onClick={() => handleCEOApprove(payment.id)}
                                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary"
                                                    >
                                                        <BsCheckCircle className="mr-1" />
                                                        Approve
                                                    </motion.button>
                                                ) : payment.ceo_approved === "accepted" ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <BsCheckCircle className="mr-1" />
                                                        Approved
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                        <BsClockHistory className="mr-1" />
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <div className="relative">
                                                    <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggle(payment.id);
                                                        }}
                                                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                                    >
                                                        <BsThreeDotsVertical size={18} className="text-gray-500" />
                                                    </motion.button>

                                                    <AnimatePresence>
                                                        {openDropdown[payment.id] && (
                                                            <motion.div
                                                                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                                                transition={{ duration: 0.2 }}
                                                                className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg z-[9999]"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <motion.button
                                                                    whileHover={{ backgroundColor: "#f3f4f6" }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown((prev) => ({ ...prev, [payment.id]: false }));
                                                                        navigate(`/dashboard/payments/${payment.id}/receipt`);
                                                                    }}
                                                                    className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100"
                                                                >
                                                                    <IoReceiptOutline size={18} className="mr-3 text-blue-600" />
                                                                    View Receipt
                                                                </motion.button>
                                                                <motion.button
                                                                    whileHover={{ backgroundColor: "#f3f4f6" }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown((prev) => ({ ...prev, [payment.id]: false }));
                                                                        setUpdatePayment({
                                                                            id: payment.id,
                                                                            amount: payment.amount,
                                                                            method: payment.method,
                                                                            transaction_id: payment.transaction_id,
                                                                            transaction_number: payment.transaction_number,
                                                                        });
                                                                        setUpdatingPayment(true);
                                                                    }}
                                                                    className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100"
                                                                >
                                                                    <CiEdit size={18} className="mr-3 text-green-600" />
                                                                    Edit Payment
                                                                </motion.button>
                                                                <motion.button
                                                                    whileHover={{ backgroundColor: "#f3f4f6" }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown((prev) => ({ ...prev, [payment.id]: false }));
                                                                        openDeleteModal(payment.id, e);
                                                                    }}
                                                                    className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-gray-50"
                                                                >
                                                                    <MdDeleteForever size={20} className="mr-3" />
                                                                    Delete Payment
                                                                </motion.button>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-16 flex flex-col items-center justify-center text-gray-500">
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            >
                                <FaExclamationCircle className="text-secondary mb-4" size={64} />
                            </motion.div>
                            <motion.h3
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.1 }}
                                className="text-xl font-semibold text-gray-700 mb-2"
                            >
                                No payments available
                            </motion.h3>
                            <motion.p
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="text-gray-500 mb-6 text-center max-w-md"
                            >
                                It looks like there are no transactions to display right now. Try adjusting your filters or add a new payment.
                            </motion.p>
                            <motion.button
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setAdd(true)}
                                className="flex items-center gap-2 px-5 py-2.5 bg-secondary text-white rounded-xl shadow-sm hover:bg-secondary/90 transition-colors"
                            >
                                <IoAdd size={18} />
                                <span>Add First Payment</span>
                            </motion.button>
                        </div>
                    )}

                    {/* Pagination */}
                    {filteredPayments && filteredPayments.length > 0 && (
                        <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
                            <div className="flex items-center mb-4 sm:mb-0">
                                <label className="text-sm text-gray-600 mr-2">Show:</label>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="border border-gray-200 rounded-lg p-2 text-sm bg-white"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={300}>300</option>
                                    <option value={1000}>1000</option>
                                </select>
                            </div>
                            <div className="flex items-center">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg ${currentPage === 1
                                            ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                                            : 'text-gray-700 bg-white hover:bg-gray-50'
                                        }`}
                                >
                                    <FaChevronLeft className="mr-2 h-4 w-4" />
                                    Previous
                                </motion.button>

                                <span className="hidden sm:flex mx-2">
                                    {currentPage > 3 && (
                                        <>
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={() => paginate(1)}
                                                className={`relative inline-flex items-center justify-center w-8 h-8 mx-1 rounded-md text-sm font-medium ${currentPage === 1
                                                        ? 'bg-primary text-white'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                1
                                            </motion.button>
                                            <span className="relative inline-flex items-center justify-center mx-1">
                                                <FaEllipsisH className="text-gray-400 w-3 h-3" />
                                            </span>
                                        </>
                                    )}

                                    {[...Array(totalPages)].map((_, i) => {
                                        const page = i + 1;
                                        if (page === currentPage || page === currentPage - 1 || page === currentPage + 1) {
                                            return (
                                                <motion.button
                                                    key={page}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    onClick={() => paginate(page)}
                                                    className={`relative inline-flex items-center justify-center w-8 h-8 mx-1 rounded-md text-sm font-medium ${currentPage === page
                                                            ? 'bg-primary text-white'
                                                            : 'text-gray-700 hover:bg-gray-100'
                                                        }`}
                                                >
                                                    {page}
                                                </motion.button>
                                            );
                                        }
                                        return null;
                                    })}

                                    {currentPage < totalPages - 2 && (
                                        <>
                                            <span className="relative inline-flex items-center justify-center mx-1">
                                                <FaEllipsisH className="text-gray-400 w-3 h-3" />
                                            </span>
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={() => paginate(totalPages)}
                                                className={`relative inline-flex items-center justify-center w-8 h-8 mx-1 rounded-md text-sm font-medium ${currentPage === totalPages
                                                        ? 'bg-primary text-white'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                {totalPages}
                                            </motion.button>
                                        </>
                                    )}
                                </span>

                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg ${currentPage === totalPages
                                            ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                                            : 'text-gray-700 bg-white hover:bg-gray-50'
                                        }`}
                                >
                                    Next
                                    <FaChevronRight className="ml-2 h-4 w-4" />
                                </motion.button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={open}
                onClose={closeDeleteModal}
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                    Delete Payment
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'text.secondary' }}>
                        Are you sure you want to delete this payment? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button
                        onClick={closeDeleteModal}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: '8px',
                            padding: '6px 16px'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: '8px',
                            padding: '6px 16px'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Receipt Modal */}
            <AnimatePresence>
                {viewReceipt && selectedPayment && (
                    <Modal title="Payment Receipt" onClose={() => setViewReceipt(false)}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            ref={printRef}
                            className="p-6 bg-white rounded-2xl shadow-lg"
                        >
                            <div className="flex justify-between items-center mb-6">
                                <img src={logo} alt="Logo" className="h-12" />
                                <h2 className="text-xl font-bold text-gray-800">Payment Receipt</h2>
                            </div>

                            <div className="mb-6 bg-gray-50 p-4 rounded-xl">
                                <p className="text-gray-600 text-sm">Transaction ID</p>
                                <h3 className="text-2xl font-bold text-gray-900">{selectedPayment.transaction_id}</h3>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <p className="text-gray-600 text-sm">Paid By</p>
                                    <h3 className="text-lg font-medium text-gray-800">
                                        {selectedPayment?.customer?.first_name} {selectedPayment?.customer?.last_name}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        #{selectedPayment?.customer?.unique_number}
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <p className="text-gray-600 text-sm">Amount</p>
                                    <h3 className="text-2xl font-bold text-green-600">
                                        {formatCurrency(selectedPayment.amount)}
                                    </h3>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <p className="text-gray-600 text-sm">Date & Time</p>
                                    <h3 className="text-lg font-medium text-gray-800">
                                        {moment(selectedPayment.createdAt).format('MMMM Do, YYYY')}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {moment(selectedPayment.createdAt).format('h:mm A')}
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <p className="text-gray-600 text-sm">Payment Method</p>
                                    <div className="flex items-center mt-1">
                                        {getMethodIcon(selectedPayment.method)}
                                        <h3 className="text-lg font-medium text-gray-800 ml-2">
                                            {selectedPayment.method}
                                        </h3>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Transaction: {selectedPayment.transaction_number}
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 bg-gray-50 p-4 rounded-xl">
                                <div className="flex items-center justify-between w-full mb-4">
                                    <div>
                                        <p className="text-gray-600 text-sm">Payment Status</p>
                                        <h3 className={`text-lg font-medium flex items-center ${selectedPayment.status === 'completed' ? 'text-green-600' : 'text-red-600'}`}>
                                            {selectedPayment.status === 'completed' ? <BsCheckCircle className="mr-2" /> : <BsXCircle className="mr-2" />}
                                            {selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1)}
                                        </h3>
                                    </div>
                                    {selectedPayment?.createdBy && (
                                        <div>
                                            <p className="text-gray-600 text-sm">Created by</p>
                                            <h3 className="text-lg font-medium text-gray-800">{selectedPayment.createdBy.first_name} {selectedPayment.createdBy.last_name}</h3>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <p className="text-gray-600 text-sm">Junior Analyst</p>
                                        <h3 className={`text-base font-medium flex items-center mt-1 ${selectedPayment.junior_analyst_approved !== 'pending' ? 'text-green-600' : 'text-orange-600'}`}>
                                            {selectedPayment.junior_analyst_approved !== 'pending' ? <BsCheckCircle className="mr-1" /> : <BsClockHistory className="mr-1" />}
                                            {selectedPayment.junior_analyst_approved !== 'pending' ? 'Approved' : 'Pending'}
                                        </h3>
                                    </div>

                                    <div>
                                        <p className="text-gray-600 text-sm">Finance</p>
                                        <h3 className={`text-base font-medium flex items-center mt-1 ${selectedPayment.finance_approved !== 'pending' ? 'text-green-600' : 'text-orange-600'}`}>
                                            {selectedPayment.finance_approved !== 'pending' ? <BsCheckCircle className="mr-1" /> : <BsClockHistory className="mr-1" />}
                                            {selectedPayment.finance_approved !== 'pending' ? 'Approved' : 'Pending'}
                                        </h3>
                                    </div>

                                    <div>
                                        <p className="text-gray-600 text-sm">CEO</p>
                                        <h3 className={`text-base font-medium flex items-center mt-1 ${selectedPayment.ceo_approved !== 'pending' ? 'text-green-600' : 'text-orange-600'}`}>
                                            {selectedPayment.ceo_approved !== 'pending' ? <BsCheckCircle className="mr-1" /> : <BsClockHistory className="mr-1" />}
                                            {selectedPayment.ceo_approved !== 'pending' ? 'Approved' : 'Pending'}
                                        </h3>
                                    </div>
                                </div>
                            </div>

                            <hr className="my-6 border-gray-200" />

                            <div className="text-sm text-center text-gray-600 bg-gray-50 p-4 rounded-xl">
                                <p>These are the details of the selected transaction. Please verify to ensure there are no discrepancies recorded.</p>
                            </div>

                            <ReactToPrint
                                trigger={() => (
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="mt-6 w-full bg-secondary text-white py-3 rounded-xl shadow-md hover:bg-secondary/90 transition flex items-center justify-center gap-2"
                                    >
                                        <IoReceiptOutline size={20} />
                                        Print Receipt
                                    </motion.button>
                                )}
                                content={() => printRef.current}
                            />
                        </motion.div>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Edit Payment Modal */}
            <AnimatePresence>
                {updatingPayment && (
                    <Modal onClose={() => setUpdatingPayment(false)} title="Update Payment">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="p-4"
                        >
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount:</label>
                                    <motion.input
                                        whileFocus={{ scale: 1.01 }}
                                        type="number"
                                        value={updatePayment.amount}
                                        required
                                        onChange={(e) => setUpdatePayment({ ...updatePayment, amount: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method:</label>
                                    <motion.select
                                        whileFocus={{ scale: 1.01 }}
                                        value={updatePayment.method}
                                        required
                                        onChange={(e) => setUpdatePayment({ ...updatePayment, method: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                                    >
                                        <option value="">Select Method</option>
                                        <option value="MPESA">MPESA</option>
                                        <option value="VISA">VISA</option>
                                        <option value="CHEQUE">CHEQUE</option>
                                        <option value="BANK_TRANSFER">BANK TRANSFER</option>
                                        <option value="CASH">CASH</option>
                                        <option value="WORLD_REMIT">WORLD REMIT</option>
                                        <option value="RTGS">RTGS</option>
                                    </motion.select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID:</label>
                                    <motion.input
                                        whileFocus={{ scale: 1.01 }}
                                        type="text"
                                        value={updatePayment.transaction_id}
                                        required
                                        onChange={(e) => setUpdatePayment({ ...updatePayment, transaction_id: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Number:</label>
                                    <motion.input
                                        whileFocus={{ scale: 1.01 }}
                                        type="number"
                                        value={updatePayment.transaction_number}
                                        required
                                        onChange={(e) => setUpdatePayment({ ...updatePayment, transaction_number: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date/Time:</label>
                                    <motion.input
                                        whileFocus={{ scale: 1.01 }}
                                        type="datetime-local"
                                        value={updatePayment.createdAt}
                                        required
                                        onChange={(e) => setUpdatePayment({ ...updatePayment, createdAt: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                                    />
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => setUpdatingPayment(false)}
                                        className="px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-xl"
                                    >
                                        Cancel
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleUpdatePayment}
                                        className="px-5 py-2.5 bg-secondary text-white font-medium rounded-xl shadow-sm"
                                    >
                                        Update Payment
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Add Payment Modal */}
            <AnimatePresence>
                {add && (
                    <Modal title="Add New Payment" onClose={() => setAdd(false)}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <AddNewPayment onClose={() => {
                                setAdd(false);
                                getPayments(currentPage, itemsPerPage);
                            }} />
                        </motion.div>
                    </Modal>
                )}
            </AnimatePresence>
        </Layout>
    );
};

export default Payments;